import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Fade } from "@mui/material";
import { keyframes } from "@emotion/react";

// Define animations
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const LoadingScreen = () => {
    const [progress, setProgress] = useState(0);
    const loadingMessages = [
        "please waiting..."
    ];
    const [messageIndex, setMessageIndex] = useState(0);

    // Simulate loading progress
    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress === 100) {
                    return 100;
                }
                const diff = Math.random() * 10;
                return Math.min(oldProgress + diff, 100);
            });
        }, 200);

        return () => {
            clearInterval(timer);
        };
    }, []);

    // Rotate loading messages
    useEffect(() => {
        const messageTimer = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        }, 3000);

        return () => {
            clearInterval(messageTimer);
        };
    }, [loadingMessages.length]);

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "linear-gradient(-45deg, #1976d2, #42a5f5, #64b5f6, #90caf9)",
                backgroundSize: "400% 400%",
                animation: `${shimmer} 15s ease infinite`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                color: "#ffffff",
                overflow: "hidden",
            }}
        >
            {/* Animated background particles */}
            {[...Array(20)].map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        width: Math.random() * 10 + 5,
                        height: Math.random() * 10 + 5,
                        background: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "50%",
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `${float} ${Math.random() * 5 + 3}s ease-in-out infinite`,
                        transform: `scale(${Math.random() * 0.5 + 0.5})`,
                    }}
                />
            ))}

            {/* Main content container */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {/* Animated logo/icon container */}
                <Box
                    sx={{
                        animation: `${float} 3s ease-in-out infinite`,
                        mb: 4,
                    }}
                >
                    <CircularProgress
                        size={80}
                        thickness={4}
                        variant="determinate"
                        value={progress}
                        sx={{
                            color: "rgba(255, 255, 255, 0.3)",
                            position: "absolute",
                        }}
                    />
                    <CircularProgress
                        size={80}
                        thickness={4}
                        variant="determinate"
                        value={progress}
                        sx={{
                            color: "#ffffff",
                            position: "relative",
                            transform: "rotate(-90deg)",
                            "& .MuiCircularProgress-circle": {
                                strokeLinecap: "round",
                                transition: "stroke-dashoffset 0.3s ease",
                            },
                        }}
                    />
                </Box>

                {/* Loading text with fade effect */}
                <Fade in={true} key={messageIndex}>
                    <Typography
                        variant="h5"
                        component="div"
                        sx={{
                            mt: 2,
                            color: "#ffffff",
                            fontWeight: 300,
                            letterSpacing: "0.1em",
                            // textTransform: "uppercase",
                            textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                            animation: `${pulse} 2s ease-in-out infinite`,
                        }}
                    >
                        {loadingMessages[messageIndex]}
                    </Typography>
                </Fade>

                {/* Progress percentage */}
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1,
                        color: "rgba(255, 255, 255, 0.8)",
                        fontWeight: 300,
                    }}
                >
                    {Math.round(progress)}%
                </Typography>

                {/* Decorative line */}
                <Box
                    sx={{
                        mt: 4,
                        width: 100,
                        height: 2,
                        background: "linear-gradient(90deg, transparent, #ffffff, transparent)",
                        animation: `${shimmer} 2s linear infinite`,
                    }}
                />
            </Box>
        </Box>
    );
};

export default LoadingScreen;